import type { components } from '../api/shema';
import { izOdgovora, napakaPovezave, NapakaApi } from '../api/napaka';

export type Uporabnik = components['schemas']['UporabnikPodatki'];
export type Instanca = components['schemas']['InstancaPodatki'];
/** Pravice uporabnika po vlogi: področje → BREZ / PREGLED / UREJANJE (glej {@code pravice.ts}). */
export type Pravice = NonNullable<components['schemas']['SejaOdgovor']['pravice']>;
type PrijavaOdgovor = components['schemas']['PrijavaOdgovor'];
type SejaOdgovor = components['schemas']['SejaOdgovor'];

/** Glava, brez katere strežnik spletne prijave ne sprejme (zaščita CSRF, glej SpletnaPrijavaKontroler). */
export const GLAVA_SPLET = 'X-Hera-Splet';

export type StanjeSeje =
  | { status: 'nalaganje' }
  | { status: 'odjavljen' }
  | {
      status: 'prijavljen';
      uporabnik: Uporabnik;
      instanca: Instanca;
      zahtevaSpremembaGesla: boolean;
      /** Skrbnik sistema (konfiguracija strežnika); skrbništvo zahteva še kodo za preverjanje. */
      skrbnik: boolean;
      /** Skrbnik je prijavljen kot ta uporabnik (iz skrbništva); konec vrne skrbnikovo sejo iz piškotka. */
      prijavaKot: boolean;
      /** Pravice po vlogi; brez podatka (starejši strežnik) vse. */
      pravice?: Pravice;
      /** Kdo ureja pravice (komu se obrniti, ko uporabnik česa ne sme). */
      urejajoPravice?: string[];
    };

/** Rezultat prijave: prijavljen ali pa mora uporabnik izbrati podjetje (več instanc). */
export type RezultatPrijave = { izbiraInstance: Instanca[] } | { prijavljen: true };

/**
 * Koliko pred potekom dostopnega žetona ga osvežimo (brskalnik časovnike v zavihkih v ozadju zamika do minute, osvežilni
 * žeton brez »Ostani prijavljen« pa velja le malo dlje od dostopnega).
 */
const PREDCASNO_MS = 5 * 60_000;

/**
 * Seja spletnega odjemalca. Dostopni žeton je samo v pomnilniku (ne v localStorage – XSS ga ne more odnesti);
 * osvežilni je v piškotku HttpOnly, ki ga pošilja brskalnik samo na /api/splet. Ob nalaganju strani
 * {@link zacni} s piškotkom dobi nov dostopni žeton, zato ponovna prijava ni potrebna.
 */
export class Seja {
  private stanje: StanjeSeje = { status: 'nalaganje' };
  private dostopniZeton: string | null = null;
  private poslusalci = new Set<() => void>();
  private osvezevanje: Promise<boolean> | null = null;
  private branjePravic: Promise<void> | null = null;
  private casovnik: ReturnType<typeof setTimeout> | undefined;
  private readonly poslji: typeof fetch;

  constructor(poslji: typeof fetch = (...a) => fetch(...a)) {
    this.poslji = poslji;
  }

  dobi = (): StanjeSeje => this.stanje;

  naroci = (poslusalec: () => void): (() => void) => {
    this.poslusalci.add(poslusalec);
    return () => this.poslusalci.delete(poslusalec);
  };

  zeton(): string | null {
    return this.dostopniZeton;
  }

  /** Ob zagonu aplikacije: obnovi sejo iz piškotka, sicer odjavljen. */
  async zacni(): Promise<void> {
    if (!(await this.osvezi())) {
      this.nastavi({ status: 'odjavljen' });
    }
  }

  /**
   * {@code ostaniPrijavljen}: piškotek ostane po zaprtju brskalnika in prijava velja do 30 dni od zadnje uporabe
   * (največ 90 dni); brez kljukice samo do zaprtja brskalnika oziroma 12 ur brez uporabe (določi strežnik).
   */
  async prijava(
    uporabniskoIme: string,
    geslo: string,
    domena?: string,
    ostaniPrijavljen = false,
  ): Promise<RezultatPrijave> {
    const o = await this.splet('prijava', { uporabniskoIme, geslo, domena, ostaniPrijavljen });
    if (o.izbiraInstance && o.izbiraInstance.length > 0 && !o.dostopniZeton) {
      return { izbiraInstance: o.izbiraInstance };
    }
    this.sprejmi(o);
    return { prijavljen: true };
  }

  /**
   * Nov dostopni žeton s piškotkom; hkratni klici počakajo na isto osvežitev (piškotek se ob vsaki zamenja,
   * dve vzporedni osvežitvi bi ga porabili dvakrat). Ob neuspehu je uporabnik odjavljen.
   */
  osvezi(): Promise<boolean> {
    if (!this.osvezevanje) {
      this.osvezevanje = this.splet('osvezi')
        .then((o) => {
          this.sprejmi(o);
          return true;
        })
        .catch((e: unknown) => {
          if (e instanceof NapakaApi && e.status === 0 && this.stanje.status === 'prijavljen') {
            // brez povezave ostanemo prijavljeni, naslednja zahteva poskusi znova
            return false;
          }
          this.pocisti();
          return false;
        })
        .finally(() => {
          this.osvezevanje = null;
        });
    }
    return this.osvezevanje;
  }

  /**
   * Prijava kot uporabnik (odgovor iz skrbništva): samo dostopni žeton, piškotek ostane skrbnikov. Ko se konča
   * ({@link koncajPrijavoKot}, potek ali 401), {@link osvezi} s piškotkom vrne skrbnikovo sejo.
   */
  prijaviKot(o: PrijavaOdgovor): void {
    this.sprejmi(o);
  }

  /** Konec prijave kot uporabnik: strežnik žeton takoj prekliče, nato spet skrbnikova seja. */
  async koncajPrijavoKot(): Promise<void> {
    const zeton = this.dostopniZeton;
    this.dostopniZeton = null;
    try {
      await this.poslji('/api/skrbnik/prijava-kot/konec', {
        method: 'POST',
        headers: zeton ? { Authorization: `Bearer ${zeton}` } : {},
      });
    } catch {
      // žeton vseeno poteče s skrbniško sejo
    }
    await this.osvezi();
  }

  async odjava(): Promise<void> {
    if (this.stanje.status === 'prijavljen' && this.stanje.prijavaKot) {
      await this.koncajPrijavoKot();
    }
    try {
      await this.splet('odjava');
    } catch {
      // piškotek bo vseeno potekel; lokalno se odjavimo v vsakem primeru
    }
    this.pocisti();
  }

  /**
   * Znova prebere vlogo in pravice (GET /api/seja), npr. ko strežnik zavrne zahtevo z NI_PRAVICE – vlogo je medtem
   * lahko spremenil skrbnik, zato meni in zasloni pokažejo trenutne pravice. Hkratni klici počakajo na enega.
   */
  osveziPravice(): Promise<void> {
    if (!this.branjePravic) {
      this.branjePravic = (async () => {
        const zeton = this.dostopniZeton;
        if (!zeton || this.stanje.status !== 'prijavljen') {
          return;
        }
        try {
          const odgovor = await this.poslji('/api/seja', { headers: { Authorization: `Bearer ${zeton}` } });
          if (odgovor.ok && this.stanje.status === 'prijavljen') {
            const o = (await odgovor.json()) as SejaOdgovor;
            this.nastavi({
              ...this.stanje,
              uporabnik: o.uporabnik ? { ...this.stanje.uporabnik, ...o.uporabnik } : this.stanje.uporabnik,
              pravice: o.pravice ?? this.stanje.pravice,
              urejajoPravice: o.urejajoPravice ?? this.stanje.urejajoPravice,
            });
          }
        } catch {
          // brez povezave ostanejo dosedanje pravice
        }
      })().finally(() => {
        this.branjePravic = null;
      });
    }
    return this.branjePravic;
  }

  /** Po sprejemu splošnih pogojev (zapisan na strežniku). */
  pogojiSprejeti(): void {
    if (this.stanje.status === 'prijavljen') {
      this.nastavi({ ...this.stanje, uporabnik: { ...this.stanje.uporabnik, splosniPogoji: true } });
    }
  }

  /** Po uspešni zamenjavi začasnega gesla. */
  gesloZamenjano(): void {
    if (this.stanje.status === 'prijavljen') {
      this.nastavi({ ...this.stanje, zahtevaSpremembaGesla: false });
    }
  }

  private sprejmi(o: PrijavaOdgovor): void {
    if (!o.dostopniZeton || !o.uporabnik || !o.instanca) {
      throw new NapakaApi('Nepričakovan odgovor strežnika pri prijavi.', 'ODGOVOR', 200);
    }
    this.dostopniZeton = o.dostopniZeton;
    clearTimeout(this.casovnik);
    if (o.potekDostopnegaZetona) {
      const cez = Math.max(o.potekDostopnegaZetona - Date.now() - PREDCASNO_MS, 10_000);
      this.casovnik = setTimeout(() => void this.osvezi(), cez);
    }
    this.nastavi({
      status: 'prijavljen',
      uporabnik: o.uporabnik,
      instanca: o.instanca,
      zahtevaSpremembaGesla: !!o.zahtevaSpremembaGesla,
      skrbnik: !!o.skrbnik,
      prijavaKot: !!o.prijavaKot,
      pravice: o.pravice,
      urejajoPravice: o.urejajoPravice,
    });
  }

  private pocisti(): void {
    this.dostopniZeton = null;
    clearTimeout(this.casovnik);
    this.nastavi({ status: 'odjavljen' });
  }

  private nastavi(stanje: StanjeSeje): void {
    this.stanje = stanje;
    this.poslusalci.forEach((p) => p());
  }

  private async splet(pot: 'prijava' | 'osvezi' | 'odjava', telo?: object): Promise<PrijavaOdgovor> {
    let odgovor: Response;
    try {
      odgovor = await this.poslji(`/api/splet/${pot}`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { [GLAVA_SPLET]: '1', ...(telo ? { 'Content-Type': 'application/json' } : {}) },
        body: telo ? JSON.stringify(telo) : undefined,
      });
    } catch {
      throw napakaPovezave();
    }
    if (!odgovor.ok) {
      throw await izOdgovora(odgovor);
    }
    return odgovor.status === 204 ? {} : ((await odgovor.json()) as PrijavaOdgovor);
  }
}

export const seja = new Seja();
